import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "../motion/Reveal";
import CountUp from "../motion/CountUp";
import { site } from "../../data/content";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section
      id="ueber"
      ref={ref}
      className="relative bg-brand-light py-32 lg:py-44 overflow-hidden isolate"
      data-testid="about-section"
    >
      <div className="max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Portrait */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative max-w-[440px] mx-auto lg:mx-0">
                <div
                  className="absolute -inset-6 rounded-[2rem] bg-brand-accent/10 blur-2xl"
                  aria-hidden
                />
                <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] bg-brand-primary shadow-[0_40px_80px_-30px_rgba(15,23,42,0.35)]">
                  <motion.img
                    style={{ y, objectPosition: "50% 20%" }}
                    src={site.portrait.url}
                    alt={site.portrait.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-[120%] w-full object-cover"
                    data-testid="about-portrait"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(15,23,42,0) 45%, rgba(15,23,42,0.15) 70%, rgba(15,23,42,0.85) 100%)",
                    }}
                    aria-hidden
                  />
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <div className="text-[11px] uppercase tracking-widest text-brand-accent">
                      Gründer & Digital Expert
                    </div>
                    <div className="mt-1 text-xl font-medium">Björn Richter</div>
                  </div>
                </div>

                {/* Signature-Chip: horizontal, mittig unter dem Portrait */}
                <div className="mt-6 flex justify-center lg:justify-start">
                  <div className="inline-flex items-center gap-3 rounded-full bg-white border border-brand-border pl-4 pr-5 py-2.5 shadow-[0_10px_30px_-15px_rgba(15,23,42,0.25)]">
                    <span className="h-2 w-2 rounded-full bg-brand-success" aria-hidden />
                    <span className="text-xs text-brand-muted">Direkt erreichbar:</span>
                    <a
                      href="mailto:hallo@metalotsen.de"
                      className="text-sm font-medium text-brand-primary hover:text-brand-accent transition-colors"
                    >
                      hallo@metalotsen.de
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Copy */}
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow">Über · Persönlich</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-balance">
                Hallo, ich bin{" "}
                <span className="text-brand-accent">Björn Richter</span>.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6 text-brand-primary/85 text-lg lg:text-xl leading-relaxed max-w-2xl">
                <p>
                  Ich bin kein Callcenter, kein Ticket-System und kein Account
                  Manager, der die eigentliche Arbeit weiterreicht. Sie
                  sprechen mit dem, der auch umsetzt — direkt aus Leipzig, seit
                  über 12 Jahren im Digitalen zuhause.
                </p>
                <p>
                  Meine Überzeugung: Digitale Sichtbarkeit ist kein
                  Selbstzweck. Sie muss zu Ihrem Geschäft passen. Ob moderne
                  Webseite, Social Media, Recruiting oder KI — mein Ziel ist
                  es, individuelle Lösungen zu entwickeln, die nachhaltig
                  wirken und Ihr Unternehmen messbar voranbringen.
                </p>
                <p className="text-brand-primary font-medium">
                  Kein Buzzword-Bingo. Keine Standard-Präsentation. Ein
                  Gespräch — und ehrliche Einschätzung, ob wir zusammenpassen.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-12 grid grid-cols-2 gap-8 max-w-lg border-t border-brand-border pt-8">
                <div>
                  <div className="text-3xl font-semibold tracking-tight tabular-nums">
                    <CountUp value="12+" />
                  </div>
                  <div className="text-sm text-brand-muted mt-1">
                    Jahre digitale Erfahrung
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold tracking-tight tabular-nums">
                    <CountUp value="1" />
                  </div>
                  <div className="text-sm text-brand-muted mt-1">
                    persönlicher Ansprechpartner
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10">
                <a
                  href="#kontakt"
                  className="btn-primary"
                  data-testid="about-cta"
                >
                  Direkt mit mir sprechen
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
